import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";
import { formatLegalRole } from "@/shared/utils/formatters";

import type { PersonContact } from "@/shared/types/estateContact";

const PROFESSIONAL_RELATIONSHIPS = ["attorney", "other-professional"];
const PROFESSIONAL_LEGAL_ROLES = ["Attorney", "Financial Advisor"];

export function formatContactName(
  firstName: string | null,
  lastName: string | null,
  middleName?: string | null,
): string {
  if (!firstName && !lastName) return "\u2014";
  return [firstName, middleName, lastName].filter(Boolean).join(" ");
}

export const contactFullName = (contact: PersonContact | undefined): string =>
  contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") : "";

function rolesLower(contact: PersonContact): string[] {
  if (!Array.isArray(contact.legal_role)) return [];
  return contact.legal_role.map((r) => r.toLowerCase());
}

export function isWillWitness(contact: PersonContact): boolean {
  const roles = rolesLower(contact);
  return (
    roles.includes("witness") || roles.includes("will witness") || roles.includes("will-witness")
  );
}

export function isExecutorContact(contact: PersonContact): boolean {
  const roles = rolesLower(contact);
  return (
    roles.includes("executor") ||
    roles.includes("co-executor") ||
    roles.includes("nominated-executor") ||
    roles.includes("nominated executor")
  );
}

// The will's nominee only, not a court-appointed executor
export function isNominatedExecutor(contact: PersonContact): boolean {
  const roles = rolesLower(contact);
  return roles.includes("nominated-executor") || roles.includes("nominated executor");
}

export function isSuccessorExecutor(contact: PersonContact): boolean {
  const roles = rolesLower(contact);
  return roles.includes("successor-executor") || roles.includes("successor executor");
}

export function isProfessionalContact(contact: PersonContact): boolean {
  return (
    PROFESSIONAL_RELATIONSHIPS.includes(contact.relationship || "") ||
    (contact.legal_role?.some((role) => PROFESSIONAL_LEGAL_ROLES.includes(role)) ?? false) ||
    !!contact.professional_role ||
    !!contact.company_or_firm
  );
}

const HIDDEN_CONTACT_EMAILS = [
  "care@elayne.com",
  "careteam@elayne.com",
  "dani+212614012026@elayne.com",
  "dani+214014012026@elayne.com",
];

function deduplicateContacts(contacts: PersonContact[]): PersonContact[] {
  const contactMap = new Map<string, PersonContact>();

  contacts.forEach((contact) => {
    const key = [contact.email || "", contact.first_name || "", contact.last_name || ""].join("|");
    const existing = contactMap.get(key);
    if (!existing || new Date(contact.created_at) > new Date(existing.created_at)) {
      contactMap.set(key, contact);
    }
  });

  return Array.from(contactMap.values());
}

function sortContactsWithMeFirst(
  contacts: PersonContact[],
  currentUserEmail?: string,
  accountOwnerEmail?: string,
): PersonContact[] {
  if (!currentUserEmail) return contacts;

  const me = currentUserEmail.toLowerCase();
  const owner = accountOwnerEmail?.toLowerCase();

  return [...contacts].sort((a, b) => {
    const aEmail = a.email?.toLowerCase();
    const bEmail = b.email?.toLowerCase();
    const aIsMe = aEmail === me;
    const bIsMe = bEmail === me;
    const aIsOwner = !!owner && aEmail === owner;
    const bIsOwner = !!owner && bEmail === owner;

    if (aIsMe && !bIsMe) return -1;
    else if (!aIsMe && bIsMe) return 1;

    if (aIsOwner && !bIsOwner) return -1;
    else if (!aIsOwner && bIsOwner) return 1;

    return 0;
  });
}

export function getVisibleContacts(
  contacts: PersonContact[],
  options: { currentUserEmail?: string; accountOwnerEmail?: string; excludeEmail?: string },
): PersonContact[] {
  const excludeEmail = options.excludeEmail?.toLowerCase();
  const visible = contacts.filter((contact) => {
    const email = contact.email?.toLowerCase();
    return (
      !email ||
      (!HIDDEN_CONTACT_EMAILS.includes(email) &&
        !email.includes("careteam") &&
        email !== excludeEmail)
    );
  });

  return sortContactsWithMeFirst(
    deduplicateContacts(visible),
    options.currentUserEmail,
    options.accountOwnerEmail,
  );
}

export function formatContactSubtitle(contact: PersonContact): string {
  const relationship = contact.relationship ? getRelationshipLabel(contact.relationship) : "";
  const roles = contact.legal_role?.length ? formatLegalRole(contact.legal_role) : "";
  return [relationship, roles].filter(Boolean).join(" \u2022 ");
}

export function matchesContactQuery(contact: PersonContact, query: string): boolean {
  const name = [contact.first_name, contact.middle_name, contact.last_name, contact.nickname]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .every((word) => name.includes(word));
}
